import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Drawer,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Tab,
  TabList,
  Tabs,
  Textarea,
  Typography,
} from "@mui/joy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatMoney, formatQuoteRange } from "../../lib/directMailApi";
import { getApiErrorMessage } from "../../lib/apiError";
import { mailWorkflows, mailPageLabel } from "./mailWorkflows";
export function BatchWorkspace({
  batchId,
  onClose,
  isAdmin,
}: {
  batchId: number | null;
  onClose: () => void;
  isAdmin: boolean;
}) {
  const [tab, setTab] = useState(0),
    [index, setIndex] = useState(0),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [outcome, setOutcome] = useState("accepted"),
    [note, setNote] = useState(""),
    [providerId, setProviderId] = useState(""),
    [verified, setVerified] = useState(false);
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ["direct-mail", "workspace", batchId],
    queryFn: () => mailWorkflows.workspace(batchId!),
    enabled: batchId !== null,
    refetchInterval: batchId !== null ? 10000 : false,
  });
  const data = query.data,
    batch = data?.batch,
    piece = data?.pieces[index];
  useEffect(() => {
    setIndex(0);
    setConfirmed(false);
    setError("");
    setNotice("");
  }, [batchId]);
  useEffect(() => {
    setConfirmed(false);
  }, [batch?.proof_version]);
  useEffect(() => {
    setNote("");
    setProviderId("");
    setVerified(false);
  }, [piece?.id]);
  const run = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await cache.invalidateQueries({ queryKey: ["direct-mail"] });
      setNotice(message);
    } catch (e) {
      setError(getApiErrorMessage(e, "The action could not be completed."));
      await query.refetch();
      setConfirmed(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer
      open={batchId !== null}
      onClose={onClose}
      anchor="right"
      size="lg"
      slotProps={{
        content: {
          sx: {
            width: { xs: "100vw", md: "min(1120px,94vw)" },
            p: 2,
            overflowY: "auto",
          },
        },
      }}
    >
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography level="h4">
            {batch?.reference || "Mailing workspace"}
          </Typography>
          <Button variant="plain" onClick={onClose}>
            Close
          </Button>
        </Stack>
        {error && <Alert color="danger">{error}</Alert>}
        {notice && <Alert color="success">{notice}</Alert>}
        {query.isError && (
          <Alert color="danger">
            {getApiErrorMessage(query.error, "Could not load the batch.")}
            <Button onClick={() => void query.refetch()}>Retry</Button>
          </Alert>
        )}
        {query.isLoading && <Typography>Loading recipients…</Typography>}
        {batch && (
          <>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              <Chip>{batch.status}</Chip>
              <Typography>
                {batch.piece_count} houses · {formatMoney(batch.unit_price)}{" "}
                each · {formatMoney(batch.total_amount)} total
              </Typography>
            </Stack>
            <Typography level="body-sm">
              {batch.paid_at
                ? "Payment recorded; recipient retries add no batch charge."
                : "Nothing charged yet."}{" "}
              {data?.included_pages.length
                ? `Pages: ${data.included_pages.map(mailPageLabel).join(", ")}`
                : "Postcard front and back"}
            </Typography>
            <Tabs value={tab} onChange={(_, value) => setTab(Number(value))}>
              <TabList>
                <Tab>Proof review</Tab>
                <Tab>Fulfillment</Tab>
              </TabList>
            </Tabs>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Select
                aria-label="Recipient"
                value={piece?.id ?? null}
                onChange={(_, id) =>
                  setIndex(data!.pieces.findIndex((row) => row.id === id))
                }
                sx={{ flex: 1, minWidth: 0 }}
              >
                {data?.pieces.map((row) => (
                  <Option key={row.id} value={row.id}>
                    {row.address}
                  </Option>
                ))}
              </Select>
              <Button
                variant="outlined"
                disabled={index === 0}
                onClick={() => setIndex(index - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={index >= (data?.pieces.length ?? 0) - 1}
                onClick={() => setIndex(index + 1)}
              >
                Next
              </Button>
            </Stack>
            {piece && (
              <>
                <Typography level="title-md">
                  {index + 1} / {data?.pieces.length} · {piece.address}
                </Typography>
                {tab === 0 ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "minmax(0,2fr) minmax(0,1fr)",
                        },
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        {piece.proof_url ? (
                          <>
                            <Box
                              component="iframe"
                              title="Recipient print proof"
                              src={`${piece.proof_url}#toolbar=0&navpanes=0&view=FitH`}
                              sx={{
                                width: "100%",
                                height: { xs: 440, md: 600 },
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "sm",
                              }}
                            />
                            <Button
                              component="a"
                              href={piece.proof_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="plain"
                            >
                              Open recipient PDF
                            </Button>
                          </>
                        ) : (
                          <Alert color="warning">
                            Generate a versioned proof to review this house.
                          </Alert>
                        )}
                      </Box>
                      <Stack spacing={2}>
                        <Typography level="title-md">
                          Before approving
                        </Typography>
                        <Typography level="body-sm">
                          Check the printed address, house image, estimate,
                          offer and disclaimer on every recipient proof.
                        </Typography>
                        <Typography>
                          Quote:{" "}
                          {formatQuoteRange(piece.quote_low, piece.quote_high)}
                        </Typography>
                        <Typography>
                          Rendered pages: {piece.pages ?? "Not rendered"}
                        </Typography>
                        <Typography
                          level="body-sm"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          QR destination: {piece.scan_url || "Not rendered"}
                        </Typography>
                        <Typography
                          level="body-xs"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          Version: {batch.proof_version || "Not rendered"}
                        </Typography>
                        {batch.proof_approved_at ? (
                          <Alert color="success">
                            Approved{" "}
                            {new Date(batch.proof_approved_at).toLocaleString()}{" "}
                            by {data?.approval_name || "an organization admin"}.
                          </Alert>
                        ) : (
                          isAdmin &&
                          batch.proof_version &&
                          !batch.paid_at && (
                            <>
                              <Checkbox
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                                label="I reviewed all recipient proofs, addresses, QR destinations and prices for this version."
                              />
                              <Button
                                loading={busy}
                                disabled={!confirmed}
                                onClick={() =>
                                  void run(
                                    () =>
                                      mailWorkflows.approve(
                                        batch.id,
                                        batch.proof_version!,
                                      ),
                                    "Proof version approved. You can now review payment and submit from Batches.",
                                  )
                                }
                              >
                                Approve this proof version
                              </Button>
                            </>
                          )
                        )}
                      </Stack>
                    </Box>
                  </>
                ) : (
                  <Stack spacing={2}>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      <Chip
                        color={
                          piece.status === "accepted"
                            ? "success"
                            : piece.status === "failed"
                              ? "danger"
                              : "neutral"
                        }
                      >
                        {piece.status}
                      </Chip>
                      <Typography>
                        {piece.provider_piece_id || "No provider ID recorded"}
                      </Typography>
                    </Stack>
                    {piece.error && (
                      <Alert color="warning">{piece.error}</Alert>
                    )}
                    <Typography level="body-sm">
                      Attempted:{" "}
                      {piece.attempted_at
                        ? new Date(piece.attempted_at).toLocaleString()
                        : "Not yet"}{" "}
                      · Mailed:{" "}
                      {piece.mailed_at
                        ? new Date(piece.mailed_at).toLocaleDateString()
                        : "Not reported"}{" "}
                      · Delivered:{" "}
                      {piece.delivered_at
                        ? new Date(piece.delivered_at).toLocaleDateString()
                        : "Not reported"}
                    </Typography>
                    {piece.events.length ? (
                      piece.events.map((event, i) => (
                        <Box
                          key={i}
                          sx={{
                            borderLeft: "2px solid",
                            borderColor: "primary.300",
                            pl: 2,
                          }}
                        >
                          <Typography level="title-sm">{event.type}</Typography>
                          <Typography level="body-xs">
                            {new Date(event.at).toLocaleString()}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography>No delivery events reported yet.</Typography>
                    )}
                    {isAdmin && batch.provider === "lob" && batch.paid_at && (
                      <>
                        <Typography level="body-sm">
                          {
                            data?.pieces.filter((p) => p.provider_piece_id)
                              .length
                          }{" "}
                          accepted ·{" "}
                          {
                            data?.pieces.filter(
                              (p) =>
                                !p.provider_piece_id &&
                                ["failed", "pending"].includes(p.status),
                            ).length
                          }{" "}
                          retryable ·{" "}
                          {
                            data?.pieces.filter((p) =>
                              ["uncertain", "dispatching"].includes(p.status),
                            ).length
                          }{" "}
                          held
                        </Typography>
                        <Button
                          variant="outlined"
                          loading={busy}
                          disabled={
                            !["failed", "printing"].includes(batch.status) ||
                            !data?.pieces.some(
                              (p) =>
                                !p.provider_piece_id &&
                                ["failed", "pending"].includes(p.status),
                            )
                          }
                          onClick={() =>
                            void run(
                              () => mailWorkflows.retry(batch.id),
                              "Outstanding pieces queued. The existing payment is reused.",
                            )
                          }
                        >
                          Retry failed / pending pieces · $0 additional batch
                          charge
                        </Button>
                        {["uncertain", "dispatching"].includes(
                          piece.status,
                        ) && (
                          <Stack
                            spacing={1}
                            sx={{
                              p: 2,
                              bgcolor: "background.level1",
                              borderRadius: "sm",
                            }}
                          >
                            <Typography level="title-md">
                              Reconcile a held request
                            </Typography>
                            <Typography level="body-sm">
                              Verify the outcome in the provider account before
                              recording it. A dispatch less than ten minutes old
                              is still protected.
                            </Typography>
                            <Select
                              value={outcome}
                              onChange={(_, v) => v && setOutcome(v)}
                              aria-label="Verified outcome"
                            >
                              <Option value="accepted">
                                Provider accepted it
                              </Option>
                              <Option value="rejected">
                                Provider confirmed no piece was created
                              </Option>
                            </Select>
                            {outcome === "accepted" && (
                              <FormControl>
                                <FormLabel>Provider piece ID</FormLabel>
                                <Input
                                  value={providerId}
                                  onChange={(e) =>
                                    setProviderId(e.target.value)
                                  }
                                />
                              </FormControl>
                            )}
                            <Textarea
                              minRows={2}
                              placeholder="Verification notes"
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                            />
                            <Checkbox
                              checked={verified}
                              onChange={(e) => setVerified(e.target.checked)}
                              label="I verified this outcome with the provider"
                            />
                            <Button
                              loading={busy}
                              disabled={
                                !verified ||
                                note.trim().length < 10 ||
                                (outcome === "accepted" && !providerId.trim())
                              }
                              onClick={() =>
                                void run(
                                  () =>
                                    mailWorkflows.reconcile(batch.id, {
                                      pin_id: piece.id,
                                      outcome,
                                      note,
                                      provider_piece_id: providerId,
                                      provider_verified: verified,
                                    }),
                                  "Verified outcome recorded.",
                                )
                              }
                            >
                              Record verified outcome
                            </Button>
                          </Stack>
                        )}
                      </>
                    )}
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
}
